import { useState, useEffect, useCallback } from 'react';
import { useLocale } from '../context/LocaleContext';

interface CopyBlock {
  id: string;
  page: string;
  section: string;
  locale: string;
  content: string;
  displayOrder: number;
  metadata: Record<string, any>;
  status: string;
}

export function useCopy(page?: string, section?: string) {
  const { locale } = useLocale();
  const [blocks, setBlocks] = useState<CopyBlock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCopy() {
      try {
        let url = `/api/data?type=copy&locale=${encodeURIComponent(locale)}`;
        if (page) url += `&page=${encodeURIComponent(page)}`;
        if (section) url += `&section=${encodeURIComponent(section)}`;

        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        setBlocks(data.copy || []);
      } catch (err) {
        console.error('[useCopy] fetch error:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchCopy();
  }, [page, section, locale]);

  const getById = useCallback(
    (id: string): CopyBlock | undefined =>
      blocks.find((b) => b.id === id || b.id === `${id}-${locale}`),
    [blocks, locale]
  );

  const getBySection = useCallback(
    (sectionName: string): CopyBlock[] =>
      blocks.filter((b) => b.section === sectionName).sort((a, b) => a.displayOrder - b.displayOrder),
    [blocks]
  );

  // Helper: get content string with fallback
  // Matches both base IDs (English) and locale-suffixed IDs (e.g. hero-headline-it)
  const text = useCallback(
    (id: string, fallback: string): string => {
      const block = blocks.find((b) => b.id === id || b.id === `${id}-${locale}`);
      return block?.content || fallback;
    },
    [blocks, locale]
  );

  return { blocks, loading, getById, getBySection, text };
}
